import { useEffect, useRef } from "react";
import playerjs from "player.js";
import { useBunnyEmbedSource } from "@/hooks/use-bunny-embed-source";
import { playerJsClock, type PlaybackClock } from "@/lib/playback-clock";

type BunnyLessonPlayerProps = {
  lessonId: string;
  title: string;
  onClock: (clock: PlaybackClock | null) => void;
  /** LRN-16: receives the API error so the player can say why (expired, not ready, …). */
  onError: (err?: unknown) => void;
};

/**
 * Bunny Stream's hosted player (INFRA-007). The embed URL is signed per request and expires, so it
 * is fetched when the lesson opens, never stored — and (LRN-23) refreshed shortly before it
 * expires, swapped in only while the video is paused so a long-open lesson can still resume.
 */
export function BunnyLessonPlayer({ lessonId, title, onClock, onError }: BunnyLessonPlayerProps) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const playingRef = useRef(false);
  /** LRN-10: last whole second seen, so a refreshed iframe (LRN-23) resumes there, not at 0:00. */
  const lastSecondsRef = useRef(0);
  const loadsRef = useRef(0);
  const { embedUrl } = useBunnyEmbedSource(lessonId, {
    onError,
    isPlaying: () => playingRef.current,
  });

  useEffect(() => {
    playingRef.current = false;
    lastSecondsRef.current = 0;
    loadsRef.current = 0;
    onClock(null);
    // Keyed on the lesson only: onClock is a stable parent callback.
  }, [lessonId]);

  if (!embedUrl) return null;

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
      <iframe
        ref={frameRef}
        data-testid="course-player-bunny"
        src={embedUrl}
        title={title}
        className="absolute inset-0 h-full w-full border-0"
        // H3: no `allowfullscreen` and no picture-in-picture — inside the iframe they would show
        // the video without the watermark; ProtectedPlayerFrame's button fills the screen instead.
        allow="accelerometer; gyroscope; autoplay; encrypted-media"
        onLoad={() => {
          if (!frameRef.current) return;
          // A refreshed URL reloads the iframe: a new player, paused until the student plays.
          playingRef.current = false;
          const player = new playerjs.Player(frameRef.current);
          player.on("play", () => {
            playingRef.current = true;
          });
          const stopped = () => {
            playingRef.current = false;
          };
          player.on("pause", stopped);
          player.on("ended", stopped);
          player.on("timeupdate", (data) => {
            const seconds = (data as { seconds?: unknown } | undefined)?.seconds;
            if (typeof seconds === "number") lastSecondsRef.current = Math.floor(seconds);
          });
          const clock = playerJsClock(player);
          // The first load is positioned by the page (resume); a reload continues where it was.
          if (loadsRef.current > 0 && lastSecondsRef.current > 0)
            clock.seek(lastSecondsRef.current);
          loadsRef.current += 1;
          onClock(clock);
        }}
      />
    </div>
  );
}
